(function () {
  const SECTION_SELECTOR = '[data-fragrance-notes]';

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });
  }

  async function ensureGsap() {
    if (window.gsap && window.ScrollTrigger) {
      return;
    }

    await loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js');
    await loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js');
  }

  function getFrameMetrics(image) {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = image.naturalWidth;
    tempCanvas.height = image.naturalHeight;

    const tempContext = tempCanvas.getContext('2d', { willReadFrequently: true });
    tempContext.drawImage(image, 0, 0);

    const { data, width, height } = tempContext.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < height; y += 2) {
      for (let x = 0; x < width; x += 2) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 12) {
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }
      }
    }

    if (maxX < minX || maxY < minY) {
      return {
        centerX: image.naturalWidth / 2,
        centerY: image.naturalHeight / 2,
      };
    }

    return {
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
    };
  }

  class FragranceNotesSection {
    constructor(root) {
      this.root = root;
      this.pin = root.querySelector('[data-fragrance-notes-pin]');
      this.track = root.querySelector('[data-track]');
      this.clip = root.querySelector('[data-track-clip]');
      this.bottleCanvas = root.querySelector('canvas[data-bottle-display]');
      this.bottleImg = root.querySelector('img[data-bottle-display]');
      this.frameUrls = this.getFrameUrls();
      this.frameCount = this.frameUrls.length;
      this.loadedFrames = [];
      this.frameMetrics = [];
      this.currentFrame = -1;
      this.scrollTrigger = null;
      this.tween = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.sliderBehavior = root.dataset.sliderBehavior || 'hide_after_bottle';
      this.onResize = this.handleResize.bind(this);
    }

    isBehindBottleMode() {
      return this.sliderBehavior === 'behind_bottle';
    }

    getFrameUrls() {
      const jsonEl = this.root.querySelector('[data-bottle-frame-urls]');

      if (jsonEl) {
        try {
          const urls = JSON.parse(jsonEl.textContent);
          if (Array.isArray(urls) && urls.length) return urls;
        } catch (error) {
          console.warn('Fragrance Notes: invalid frame JSON', error);
        }
      }

      return Array.from(this.root.querySelectorAll('[data-bottle-frame]')).map((frame) => frame.src);
    }

    loadImage(url) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.decoding = 'async';
        if (url.indexOf('/files/') !== -1) {
          image.crossOrigin = 'anonymous';
        }
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error(`Failed to load frame: ${url}`));
        image.src = url;
      });
    }

    async preloadFrames() {
      if (!this.frameCount || this.loadedFrames.length) return;

      const images = await Promise.all(this.frameUrls.map((url) => this.loadImage(url)));
      this.loadedFrames = images;
      this.frameMetrics = images.map((image) => getFrameMetrics(image));
    }

    setupCanvas() {
      if (!this.bottleCanvas) return;

      const viewport = this.bottleCanvas.parentElement;
      if (!viewport) return;

      const dpr = window.devicePixelRatio || 1;
      const width = viewport.clientWidth;
      const height = viewport.clientHeight;

      this.bottleCanvas.width = Math.round(width * dpr);
      this.bottleCanvas.height = Math.round(height * dpr);
      this.bottleCanvas.style.width = `${width}px`;
      this.bottleCanvas.style.height = `${height}px`;

      const context = this.bottleCanvas.getContext('2d');
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);
    }

    drawFrame(index) {
      if (!this.bottleCanvas || !this.loadedFrames.length) return;

      const frameIndex = Math.min(Math.max(index, 0), this.loadedFrames.length - 1);
      const image = this.loadedFrames[frameIndex];
      const metrics = this.frameMetrics[frameIndex];
      const context = this.bottleCanvas.getContext('2d');
      const width = this.bottleCanvas.clientWidth;
      const height = this.bottleCanvas.clientHeight;

      context.clearRect(0, 0, width, height);

      const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
      const drawWidth = image.naturalWidth * scale;
      const drawHeight = image.naturalHeight * scale;
      const x = width / 2 - metrics.centerX * scale;
      const y = height / 2 - metrics.centerY * scale;

      context.drawImage(image, x, y, drawWidth, drawHeight);
    }

    destroy() {
      window.removeEventListener('resize', this.onResize);

      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }

      if (this.tween) {
        this.tween.kill();
        this.tween = null;
      }

      if (this.track) {
        gsap.set(this.track, { clearProps: 'transform' });
      }

      this.currentFrame = -1;
    }

    getTrackWidth() {
      if (!this.track) return 0;

      const items = this.track.querySelectorAll('.fragrance-notes__item');
      if (!items.length) return 0;

      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;

      let trackWidth = paddingLeft + paddingRight;
      items.forEach((item, index) => {
        trackWidth += item.offsetWidth;
        if (index < items.length - 1) trackWidth += gap;
      });

      return trackWidth;
    }

    getMaxScroll() {
      if (!this.track || !this.clip) return 0;

      const items = this.track.querySelectorAll('.fragrance-notes__item');
      if (!items.length) return 0;

      const clipWidth = this.clip.clientWidth;
      const trackWidth = this.getTrackWidth();
      const measuredScroll = Math.max(0, trackWidth - clipWidth);

      if (this.isBehindBottleMode()) {
        const scrollWidthScroll = Math.max(0, this.track.scrollWidth - clipWidth);
        return Math.max(measuredScroll, scrollWidthScroll);
      }

      const lastItem = items[items.length - 1];
      const lastItemRight = lastItem.offsetLeft + lastItem.offsetWidth;

      return Math.max(0, lastItemRight);
    }

    getHeaderOffset() {
      return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    }

    getScrollDistance() {
      const maxScroll = this.getMaxScroll();
      const frameScroll = this.frameCount > 1 ? (this.frameCount - 1) * 4 : 0;
      return Math.max(maxScroll, frameScroll, 320);
    }

    setFrame(index) {
      if (!this.frameCount) return;

      const frameIndex = Math.min(Math.max(index, 0), this.frameCount - 1);
      if (this.currentFrame === frameIndex) return;

      this.currentFrame = frameIndex;

      if (this.bottleCanvas && this.loadedFrames.length) {
        this.drawFrame(frameIndex);
        return;
      }

      if (this.bottleImg) {
        this.bottleImg.src = this.frameUrls[frameIndex];
        return;
      }

      const stackedFrames = this.root.querySelectorAll('[data-bottle-frame]');
      stackedFrames.forEach((frame, i) => {
        frame.classList.toggle('is-active', i === frameIndex);
      });
    }

    handleResize() {
      if (this.bottleCanvas && this.loadedFrames.length) {
        this.setupCanvas();
        this.drawFrame(this.currentFrame >= 0 ? this.currentFrame : 0);
      }

      if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
      }
    }

    initStaticFallback() {
      this.setFrame(0);
    }

    async initScrollAnimation() {
      if (!this.pin || !this.track || !this.clip || this.reducedMotion) {
        if (this.bottleCanvas) {
          await this.preloadFrames()
            .then(() => {
              this.setupCanvas();
              this.setFrame(0);
            })
            .catch(() => {});
        } else {
          this.initStaticFallback();
        }
        return;
      }

      if (this.bottleCanvas) {
        await this.preloadFrames().catch(() => {});
        this.setupCanvas();
        this.setFrame(0);
      }

      const headerOffset = this.getHeaderOffset();

      this.tween = gsap.to(this.track, {
        x: () => -this.getMaxScroll(),
        ease: 'none',
        scrollTrigger: {
          trigger: this.root,
          start: () => `top ${headerOffset}px`,
          end: () => `+=${this.getScrollDistance()}`,
          pin: this.root,
          pinSpacing: true,
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            if (this.frameCount <= 1) return;

            const frameIndex = Math.round(self.progress * (this.frameCount - 1));
            this.setFrame(frameIndex);
          },
        },
      });

      this.scrollTrigger = this.tween.scrollTrigger;
      window.addEventListener('resize', this.onResize);
      this.setFrame(0);
    }

    refresh() {
      this.frameUrls = this.getFrameUrls();
      this.frameCount = this.frameUrls.length;
      this.loadedFrames = [];
      this.frameMetrics = [];
      this.destroy();
      this.initScrollAnimation();
    }
  }

  const instances = new Map();

  function initSection(root) {
    if (!root) return;

    destroySection(root);

    const instance = new FragranceNotesSection(root);
    instances.set(root, instance);

    ensureGsap()
      .then(() => {
        window.gsap.registerPlugin(window.ScrollTrigger);
        return instance.initScrollAnimation();
      })
      .catch(() => {
        instance.initStaticFallback();
      });
  }

  function destroySection(root) {
    const instance = instances.get(root);
    if (!instance) return;

    instance.destroy();
    instances.delete(root);
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });

  window.addEventListener('resize', () => {
    if (!window.ScrollTrigger) return;

    window.ScrollTrigger.refresh();
  });
})();
